import React, { useState } from "react"
import { graphql } from "gatsby"
import { getImage } from "gatsby-plugin-image"

import findImage from "@/utils/find-image"

import MainLayout from "@/layouts/MainLayout"
import SharedHead from "@/components/SharedHead"

import ProductHeader from "@/components/ProductHeader"
import G2ReviewsCarousel from "@/components/G2ReviewsCarousel"
import CustomerStorySnapshot from "@/components/CustomerStorySnapshot"
import FeatureDemoTabsHeader, {
  FeatureDemoTab,
} from "@/components/FeatureDemoTabsHeader"
import FeatureDemoNoTabs from "@/components/FeatureDemoNoTabs"
import WebinarSignupBanner from "@/components/WebinarSignupBanner"
import ImageWithTextBlockContainer from "@/components/ImageWithTextBlockContainer"
import PricingSnapshot from "@/components/PricingSnapshot"
import ImageWithTextBlock from "@/components/ImageWithTextBlock"
import ExploreSlider from "@/components/ExploreSlider"
import Divider from "@/components/Divider"

// Icons
import AutomationsIcon from "@/components/Automations"
import TasksAndCalendarIcon from "@/components/TasksAndCalendar"
import ContactManagementIcon from "@/components/ContactManagement"
import ProjectsIcon from "@/components/Projects"
import ReportingIcon from "@/components/Reporting"

import MakingMenopauseWorkImage from "@/icons/customer-story-logos/MMW-image.svg"

import {
  LuTag,
  LuCalendar1,
  LuRepeat,
  LuChartNoAxesCombined,
} from "react-icons/lu"

import { Trans, useTranslation } from "@zestia/gatsby-plugin-react-i18next"

import useHeadTranslation from "@/hooks/use-head-translation"

export const Head = props => {
  const { t } = useHeadTranslation(props)

  return (
    <SharedHead
      {...props}
      title={t("drive-efficiency.meta-title")}
      description={t("drive-efficiency.meta-description")}
    />
  )
}

const EfficiencyPage = ({ data }) => {
  const { t } = useTranslation()
  const [tier, setTier] = useState()
  const [frequency, setFrequency] = useState("ANNUAL")

  return (
    <MainLayout
      trackingData={{
        content_category: "feature",
        intent: "tofu",
      }}
    >
      <ProductHeader
        kicker={t("drive-efficiency.header.kicker")}
        title={t("drive-efficiency.header.title")}
        description={t("drive-efficiency.header.description")}
        links={[
          {
            to: "#start-selling",
            text: t("drive-efficiency.header.links.faster-sales"),
          },
          {
            to: "#be-everywhere",
            text: t("drive-efficiency.header.links.client-onboarding"),
          },
          {
            to: "#your-deals-revealed",
            text: t("drive-efficiency.header.links.goodbye-admin"),
          },
        ]}
        image={{
          src: findImage(data.allFile, "new-client-automation"),
          alt: t("drive-efficiency.header.image-alt"),
        }}
      />
      <G2ReviewsCarousel
        reviews={t("home.g2-review-quotes", { returnObjects: true })}
      />

      <FeatureDemoTabsHeader>
        <FeatureDemoTab
          name={t("drive-efficiency.featureTabs.tab-1.tab-title")}
          isDefault="true"
          id="start-selling"
          kicker={t("drive-efficiency.featureTabs.tab-1.kicker")}
          title={t("drive-efficiency.featureTabs.tab-1.title")}
          description={
            <Trans
              i18nKey="drive-efficiency.featureTabs.tab-1.description"
              components={{ em: <em /> }}
            />
          }
          image={{
            src: findImage(data.allFile, "sales-pipeline-list-edit-milestone"),
            alt: t("drive-efficiency.featureTabs.tab-1.image-alt"),
          }}
        />
        <FeatureDemoTab
          name={t("drive-efficiency.featureTabs.tab-2.tab-title")}
          id="be-everywhere"
          kicker={t("drive-efficiency.featureTabs.tab-2.kicker")}
          title={t("drive-efficiency.featureTabs.tab-2.title")}
          description={t("drive-efficiency.featureTabs.tab-2.description")}
          image={{
            src: findImage(data.allFile, "integrations-wall"),
            alt: t("drive-efficiency.featureTabs.tab-2.image-alt"),
          }}
        />
        <FeatureDemoTab
          name={t("drive-efficiency.featureTabs.tab-3.tab-title")}
          id="your-deals-revealed"
          kicker={t("drive-efficiency.featureTabs.tab-3.kicker")}
          title={t("drive-efficiency.featureTabs.tab-3.title")}
          description={t("drive-efficiency.featureTabs.tab-3.description")}
          image={{
            src: findImage(data.allFile, "project-brief-email-template"),
            alt: t("drive-efficiency.featureTabs.tab-3.image-alt"),
          }}
        />
      </FeatureDemoTabsHeader>

      <CustomerStorySnapshot
        alt={t("customer-story-snapshot.making-menopause-work-2.alt")}
        name={t("customer-story-snapshot.making-menopause-work-2.name")}
        title={t("customer-story-snapshot.making-menopause-work-2.title")}
        companyName={t(
          "customer-story-snapshot.making-menopause-work-2.company",
        )}
        quote={t("customer-story-snapshot.making-menopause-work-2.quote")}
        link="/resources/customer-stories/making-menopause-work/"
        backgroundImage={getImage(findImage(data.allFile, "mmw-bg"))}
        svg={MakingMenopauseWorkImage}
        imageHeight={63}
        imageWidth={280}
      />

      <Divider />

      <FeatureDemoNoTabs
        title={t("drive-efficiency.feature-demo.title")}
        description={t("drive-efficiency.feature-demo.description")}
        image={getImage(findImage(data.allFile, "conversion-dashboard"))}
        imageAlt={t("drive-efficiency.feature-demo.image-alt")}
        highlights={[
          {
            title: t("drive-efficiency.feature-demo.highlight-1.title"),
            description: t(
              "drive-efficiency.feature-demo.highlight-1.description",
            ),
            icon: LuTag,
          },
          {
            title: t("drive-efficiency.feature-demo.highlight-2.title"),
            description: t(
              "drive-efficiency.feature-demo.highlight-2.description",
            ),
            icon: LuCalendar1,
          },
          {
            title: t("drive-efficiency.feature-demo.highlight-3.title"),
            description: t(
              "drive-efficiency.feature-demo.highlight-3.description",
            ),
            icon: LuRepeat,
          },
          {
            title: t("drive-efficiency.feature-demo.highlight-4.title"),
            description: t(
              "drive-efficiency.feature-demo.highlight-4.description",
            ),
            icon: LuChartNoAxesCombined,
          },
        ]}
      />

      <Divider width="1240" />

      <WebinarSignupBanner />

      <PricingSnapshot
        title={t("drive-efficiency.pricing-snapshot.title")}
        description={t("drive-efficiency.pricing-snapshot.description")}
        frequency={frequency}
        onSelectPlan={setTier}
        onTogglePlanFrequency={setFrequency}
        showFrequencyToggle={true}
        tier={tier}
        closeModal={() => setTier(null)}
        className="pb-20"
      />

      <Divider width="1240" noTopMargin />
      <ImageWithTextBlockContainer columnLayout={3}>
        <ImageWithTextBlock
          title={t("drive-efficiency.why-capsule.real-help.title")}
          text={t("drive-efficiency.why-capsule.real-help.description")}
          image={{
            src: getImage(findImage(data.allFile, "support")),
            alt: t("drive-efficiency.why-capsule.real-help.alt"),
          }}
        />
        <ImageWithTextBlock
          title={t("drive-efficiency.why-capsule.mobile-app.title")}
          text={t("drive-efficiency.why-capsule.mobile-app.description")}
          image={{
            src: getImage(findImage(data.allFile, "mobile")),
            alt: t("drive-efficiency.why-capsule.mobile-app.alt"),
          }}
        />
        <ImageWithTextBlock
          title={t("drive-efficiency.why-capsule.get-started.title")}
          text={t("drive-efficiency.why-capsule.get-started.description")}
          image={{
            src: getImage(findImage(data.allFile, "import")),
            alt: t("drive-efficiency.why-capsule.get-started.alt"),
          }}
        />
      </ImageWithTextBlockContainer>

      <ExploreSlider
        title={t("drive-efficiency.explore-slider.title")}
        slides={[
          {
            title: t(
              "drive-efficiency.explore-slider.tasks-and-calendar.title",
            ),
            description: t(
              "drive-efficiency.explore-slider.tasks-and-calendar.description",
            ),
            link: "/features/tasks-and-calendar/",
            icon: TasksAndCalendarIcon,
            explore: t(
              "drive-efficiency.explore-slider.tasks-and-calendar.explore",
            ),
          },
          {
            title: t(
              "drive-efficiency.explore-slider.workflow-automation.title",
            ),
            description: t(
              "drive-efficiency.explore-slider.workflow-automation.description",
            ),
            link: "/features/workflow-automation/",
            icon: AutomationsIcon,
            explore: t(
              "drive-efficiency.explore-slider.workflow-automation.explore",
            ),
          },
          {
            title: t(
              "drive-efficiency.explore-slider.contact-management.title",
            ),
            description: t(
              "drive-efficiency.explore-slider.contact-management.description",
            ),
            link: "/features/contact-management-software/",
            icon: ContactManagementIcon,
            explore: t(
              "drive-efficiency.explore-slider.contact-management.explore",
            ),
          },
          {
            title: t("drive-efficiency.explore-slider.projects.title"),
            description: t(
              "drive-efficiency.explore-slider.projects.description",
            ),
            link: "/features/project-management-crm/",
            icon: ProjectsIcon,
            explore: t("drive-efficiency.explore-slider.projects.explore"),
          },
          {
            title: t("drive-efficiency.explore-slider.sales-reports.title"),
            description: t(
              "drive-efficiency.explore-slider.sales-reports.description",
            ),
            link: "/features/sales-analytics/",
            icon: ReportingIcon,
            explore: t("drive-efficiency.explore-slider.sales-reports.explore"),
          },
        ]}
      />
    </MainLayout>
  )
}

export const query = graphql`
  query {
    ...Locales

    allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativeDirectory: {
          in: [
            "industries"
            "close-more-deals"
            "homepage"
            "customer-stories/snapshot-imagery"
          ]
        }
      }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }
`
export default EfficiencyPage
